export enum SoftwareVersionTypeEnum {
  APPLICATION_VERSION = 'App',
  SYSTEM_PACKAGE_VERSION = 'System package',
}

export const enumSoftwareVersionTypeV2 = {
  APPLICATION_VERSION: 'APPLICATION_VERSION',
  SYSTEM_PACKAGE_VERSION: 'SYSTEM_PACKAGE_VERSION',
} as const;
export type EnumSoftwareVersionType = (typeof enumSoftwareVersionTypeV2)[keyof typeof enumSoftwareVersionTypeV2];

export interface ModuleResource {
  readonly id: string;
  readonly name: string;
  readonly description?: string;
  readonly softwareVersion: {
    readonly id: string;
    readonly type: SoftwareVersionTypeEnum;
    readonly name: string;
    readonly version: string;
  };
  readonly parameters: {
    readonly parameterSetId: string;
  }[];
  readonly createdBy: {
    readonly id: string;
    readonly name: string;
  };
  readonly creationDate: string;
  readonly updatedBy: {
    readonly id: string;
    readonly name: string;
  };
  readonly updatedDate: string;
}

export interface ModulePagination {
  readonly itemsPerPage?: number;
  readonly startIndex?: number;
  readonly totalResults?: number;
}

export interface ConfigurationModulesList {
  readonly pagination: ModulePagination;
  readonly data: ModuleResource[];
}

export declare const configurationModuleSoftwareVersionType: {
  readonly APPLICATION_VERSION: 'APPLICATION_VERSION';
  readonly SYSTEM_PACKAGE_VERSION: 'SYSTEM_PACKAGE_VERSION';
};
export type ConfigurationModuleSoftwareVersionType =
  (typeof configurationModuleSoftwareVersionType)[keyof typeof configurationModuleSoftwareVersionType];

export interface ConfigurationModuleResourceSoftwareVersion {
  readonly id: string;
  readonly type: ConfigurationModuleSoftwareVersionType;
}

export declare const configurationModuleParameterSetFieldValueType: {
  readonly SINGLE: 'SINGLE';
  readonly STRUCTURE: 'STRUCTURE';
  readonly SMART_FIELD: 'SMART_FIELD';
};
export type ConfigurationModuleParameterSetFieldValueType =
  (typeof configurationModuleParameterSetFieldValueType)[keyof typeof configurationModuleParameterSetFieldValueType];

export interface ConfigurationModuleStructureParameterValueCell {
  readonly columnId: string;
  readonly fieldValue: ConfigurationModuleParameterSetFieldValue;
}

export interface ConfigurationModuleStructureParameterValue {
  readonly rowId: string;
  readonly cells: ConfigurationModuleStructureParameterValueCell[];
}

export interface ConfigurationModuleParameterSetValueSmartField {
  readonly id: string;
  readonly name?: string;
}

export interface ConfigurationModuleParameterSetFieldValue {
  readonly single?: string;
  readonly smartField?: ConfigurationModuleParameterSetValueSmartField;
  readonly structure?: ConfigurationModuleStructureParameterValue[];
  readonly type: ConfigurationModuleParameterSetFieldValueType;
}

export interface ConfigurationModuleParameterSetValue {
  readonly fieldId: string;
  readonly fieldValue: ConfigurationModuleParameterSetFieldValue;
}

export interface ConfigurationModuleParameterSet {
  readonly template: {
    readonly id: string;
  };
  readonly values: ConfigurationModuleParameterSetValue[];
}

export interface ConfigurationModuleResource {
  readonly description?: string;
  readonly id: string;
  readonly name: string;
  readonly softwareVersion: ConfigurationModuleResourceSoftwareVersion;
  readonly parameterSets: ConfigurationModuleParameterSet[];
  readonly createdBy?: string;
  readonly creationDate?: string;
  readonly updatedBy?: string;
  readonly updatedDate?: string;
}

export interface ConfigurationModuleResourceBffSoftwreVersion {
  readonly id: string;
  readonly softwareName: string;
  readonly softwareVersionNumber: string;
  readonly type: ConfigurationModuleSoftwareVersionType;
  readonly softwareId: string;
}

export interface ConfigurationModuleResourceBffModifiedByInfo {
  readonly id: string;
  readonly name: string;
}

export interface ConfigurationModuleResourceBffParameters {
  readonly parameterSetId?: string;
  readonly parameterSets: ConfigurationModuleParameterSet[];
}

export interface ConfigurationModuleResourceBff {
  readonly createdBy: ConfigurationModuleResourceBffModifiedByInfo;
  readonly creationDate: string;
  readonly description?: string;
  readonly id: string;
  readonly name: string;
  readonly softwareVersion: ConfigurationModuleResourceBffSoftwreVersion;
  readonly updatedBy: ConfigurationModuleResourceBffModifiedByInfo;
  readonly updatedDate: string;
  readonly parameters: ConfigurationModuleResourceBffParameters;
}

export interface ConfigurationModule {
  readonly creationDate: string;
  readonly description?: string;
  readonly id: string;
  readonly name: string;
  readonly updatedDate: string;
  readonly createdBy: ConfigurationModuleResourceBffModifiedByInfo;
  readonly softwareVersion: {
    readonly id: string;
    readonly softwareId: string;
    readonly softwareLogo: {
      readonly id?: string;
      readonly type?: SoftwareLogoEnum;
    };
    readonly softwareName: string;
    readonly softwareStatus: SoftwareVersionStatusEnum;
    readonly softwareVersionNumber: string;
    readonly softwareVersionStatus: SoftwareVersionStatusEnum;
    readonly type: EnumSoftwareVersionType;
  };
  readonly updatedBy: ConfigurationModuleResourceBffModifiedByInfo;
  readonly parameterSets: ParameterSets[];
}

export const logoType = {
  IMAGE: 'IMAGE',
  LOGO: 'LOGO',
  VIDEO: 'VIDEO',
} as const;
export type SoftwareLogoEnum = (typeof logoType)[keyof typeof logoType];

export const enumVersionPublicationStatus = {
  DEPRECATED: 'DEPRECATED',
  PUBLISHED: 'PUBLISHED',
} as const;
export type SoftwareVersionStatusEnum =
  (typeof enumVersionPublicationStatus)[keyof typeof enumVersionPublicationStatus];

export interface ParameterSets {
  readonly id: string;
  readonly templateId: string;
  readonly values: ConfigurationModuleParameterSetValue[];
}

export interface ConfigurationModuleMinimalResourceFront {
  readonly name: string;
  readonly description?: string;
  readonly softwareType?: ConfigurationModuleSoftwareVersionType;
  readonly application: string;
  readonly applicationVersion: string;
}

export interface DuplicateModuleBody {
  readonly name?: string;
  readonly softwareType?: ConfigurationModuleSoftwareVersionType;
  readonly softwareVersionId?: string;
  readonly isUpgrade?: boolean;
}

export interface TemplateIdsPair {
  readonly oldIdToMatch: string;
  readonly newId: string;
}

export interface ModuleVersion {
  readonly id: string;
  readonly versionNumber: string;
}

export interface CreateConfigurationModuleParams
  extends Pick<
    ConfigurationModuleMinimalResourceFront,
    'applicationVersion' | 'description' | 'name' | 'softwareType'
  > {
  readonly templateIds: string[];
}

export type TemplateValues = ConfigurationModuleResourceBff['parameters']['parameterSets'];
export interface ConfigurationModuleEditResource {
  readonly name: string;
}
